import type { NoteRecord } from '@/types/note';
import type { ExamGroup, StudentGroup } from '@/utils/noteGroups';
import NoteSheet from './NoteSheet';

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd}`;
}

function CheckMark({ checked }: { checked: boolean }) {
  return (
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${
        checked ? 'border-primary-500 bg-primary-500 text-background-50' : 'border-background-400 bg-background-50'
      }`}
    >
      {checked && <i className="ri-check-line text-[11px] leading-none"></i>}
    </span>
  );
}

interface StudentNotesGroupProps {
  group: StudentGroup;
  expandedKeys: Set<string>;
  selectedKeys: Set<string>;
  onToggleExpand: (examKey: string) => void;
  onToggleSelect: (examKey: string) => void;
  onDeleteExam: (group: StudentGroup, exam: ExamGroup) => void;
  onChangeNote: (id: string, patch: Partial<NoteRecord>) => void;
  onDeleteNote: (id: string) => void;
}

/**
 * 열린 학생 파일 안의 시험 목록입니다.
 * 시험 이름을 누르면 그 시험의 오답노트가 펼쳐집니다.
 */
export default function StudentNotesGroup({
  group,
  expandedKeys,
  selectedKeys,
  onToggleExpand,
  onToggleSelect,
  onDeleteExam,
  onChangeNote,
  onDeleteNote,
}: StudentNotesGroupProps) {
  const selKey = (examKey: string) => `${group.key}::${examKey}`;

  return (
    <section className="rounded-lg border border-background-200 bg-white">
      <ul className="divide-y divide-background-200">
        {group.exams.map((exam) => {
          const expanded = expandedKeys.has(selKey(exam.key));
          const selected = selectedKeys.has(selKey(exam.key));
          return (
            <li key={exam.key}>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 md:px-5">
                <button
                  type="button"
                  onClick={() => onToggleSelect(selKey(exam.key))}
                  aria-label="시험 선택"
                  className="flex cursor-pointer items-center"
                >
                  <CheckMark checked={selected} />
                </button>

                <button
                  type="button"
                  onClick={() => onToggleExpand(selKey(exam.key))}
                  className="group flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                >
                  <i className={`${expanded ? 'ri-folder-open-line' : 'ri-folder-line'} text-base text-accent-600`}></i>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-foreground-900 underline-offset-4 group-hover:text-primary-700 group-hover:underline">
                      {exam.examName}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-foreground-500">
                      <span className="rounded-full border border-secondary-200 bg-secondary-50 px-2 py-0.5 font-semibold text-secondary-800">
                        {exam.examType}
                      </span>
                      <span>{exam.subject}</span>
                      <span className="text-foreground-300">·</span>
                      <span>오답 {exam.notes.length}개</span>
                      <span className="text-foreground-300">·</span>
                      <span>{formatDate(exam.latestAt)}</span>
                    </span>
                  </span>
                  <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} ml-1 text-base text-foreground-400`}></i>
                </button>

                <button
                  type="button"
                  onClick={() => onDeleteExam(group, exam)}
                  className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-400 transition hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
                  aria-label="이 시험 삭제"
                >
                  <i className="ri-delete-bin-6-line text-sm"></i>
                </button>
              </div>

              {/* 펼친 시험의 오답노트 */}
              {expanded && (
                <div className="border-t border-background-200 bg-background-100/60 px-3 py-5 md:px-5">
                  <div className="flex flex-col items-center gap-8">
                    {exam.notes.map((note) => (
                      <NoteSheet
                        key={note.id}
                        note={note}
                        onChange={(patch) => onChangeNote(note.id, patch)}
                        onDelete={() => onDeleteNote(note.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}